/* Onyx Armaments — small custom elements for the Onyx sections.
   Each element sets itself up in connectedCallback and tears down in
   disconnectedCallback, so the theme editor can add, remove, duplicate and
   reorder sections without leaking listeners or sharing state. */
(function () {
  'use strict';

  /* <onyx-faq-search> — filters the FAQ rows that live inside the same section. */
  class OnyxFaqSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.status = this.querySelector('[data-faq-status]');
      this.root = this.closest('[data-onyx-faq]');
      if (!this.input || !this.root) return;
      this.onInput = this.apply.bind(this);
      this.onReset = (event) => { if (event.target.closest('[data-faq-reset]')) { this.input.value = ''; this.apply(); this.input.focus(); } };
      this.input.addEventListener('input', this.onInput);
      this.root.addEventListener('click', this.onReset);
      const form = this.input.closest('form');
      if (form) { this.onSubmit = (e) => e.preventDefault(); form.addEventListener('submit', this.onSubmit); }
      this.apply();
    }
    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('input', this.onInput);
      if (this.root) this.root.removeEventListener('click', this.onReset);
      const form = this.input && this.input.closest('form');
      if (form && this.onSubmit) form.removeEventListener('submit', this.onSubmit);
    }
    apply() {
      const query = this.input.value.trim().toLowerCase();
      const tokens = query.split(/\s+/).filter(Boolean);
      const items = Array.from(this.root.querySelectorAll('[data-faq-item]'));
      let visible = 0;
      items.forEach((item) => {
        const text = item.textContent.toLowerCase();
        const match = tokens.every((t) => text.indexOf(t) > -1);
        item.hidden = !match;
        if (match) visible += 1;
      });
      this.root.querySelectorAll('[data-faq-group]').forEach((group) => {
        const any = Array.from(group.querySelectorAll('[data-faq-item]')).some((i) => !i.hidden);
        group.hidden = !any;
        const nav = this.root.querySelector('[data-faq-nav] a[href="#' + group.id + '"]');
        if (nav) nav.hidden = !any;
      });
      const empty = this.root.querySelector('[data-faq-empty]');
      if (empty) empty.hidden = visible > 0;
      const term = this.root.querySelector('[data-faq-term]');
      if (term) term.textContent = this.input.value.trim();
      const clear = this.root.querySelector('[data-faq-clear]');
      if (clear) clear.hidden = !query;
      if (this.status) this.status.textContent = query ? visible + ' of ' + items.length + ' questions match "' + this.input.value.trim() + '"' : '';
    }
  }
  if (!customElements.get('onyx-faq-search')) customElements.define('onyx-faq-search', OnyxFaqSearch);

  /* <onyx-bundle-tiers> — quantity tiers in the buy box. Picking a tier writes the
     quantity into the product form's quantity input (so add-to-cart and the sticky bar
     use it); editing the quantity input by hand re-selects the matching tier. */
  class OnyxBundleTiers extends HTMLElement {
    connectedCallback() {
      this.onChange = (event) => {
        const input = event.target.closest('.oa-bundle__input');
        if (!input) return;
        this.setQuantity(parseInt(input.dataset.qty, 10) || 1);
        this.paint();
      };
      this.onQuantity = (event) => {
        if (event.target && event.target.closest && event.target.closest('onyx-bundle-tiers')) return;
        this.syncFromForm();
      };
      this.addEventListener('change', this.onChange);
      document.addEventListener('quantity-selector:update', this.onQuantity);
      document.addEventListener('change', this.onQuantity);
      this.syncFromForm();
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('quantity-selector:update', this.onQuantity);
      document.removeEventListener('change', this.onQuantity);
    }
    quantityInput() {
      const section = this.closest('.shopify-section') || document;
      return section.querySelector('input[name="quantity"][ref="quantityInput"], input[name="quantity"]');
    }
    setQuantity(qty) {
      const input = this.quantityInput();
      if (!input) return;
      if (parseInt(input.value, 10) === qty) return;
      input.value = qty;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('blur', { bubbles: true }));
    }
    syncFromForm() {
      const input = this.quantityInput();
      const qty = input ? parseInt(input.value, 10) || 1 : 1;
      const radios = Array.from(this.querySelectorAll('.oa-bundle__input'));
      if (!radios.length) return;
      let match = radios.find((r) => parseInt(r.dataset.qty, 10) === qty);
      if (!match) {
        /* Highest tier at or below the typed quantity, so 4 units still shows the 3+ saving. */
        const below = radios.filter((r) => parseInt(r.dataset.qty, 10) <= qty).sort((a, b) => b.dataset.qty - a.dataset.qty);
        match = below[0] || radios[0];
      }
      radios.forEach((r) => { r.checked = r === match; });
      this.paint();
    }
    paint() {
      this.querySelectorAll('.oa-bundle__tier').forEach((tier) => {
        const input = tier.querySelector('.oa-bundle__input');
        tier.classList.toggle('is-selected', !!(input && input.checked));
      });
    }
  }
  if (!customElements.get('onyx-bundle-tiers')) customElements.define('onyx-bundle-tiers', OnyxBundleTiers);

  /* <onyx-tabs> — desktop tab bar over the <details> panels of the product tabs section.
     Without JS (or on mobile) the panels stay a native accordion. */
  class OnyxTabs extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-oa-tablist]');
      this.panels = Array.from(this.querySelectorAll('details[data-oa-tab]'));
      if (!this.bar || !this.panels.length) return;
      this.mq = window.matchMedia('(min-width: 750px)');
      this.bar.innerHTML = '';
      this.tabs = this.panels.map((panel, i) => {
        const tab = document.createElement('button');
        tab.type = 'button'; tab.className = 'oa-ptabs__tab'; tab.setAttribute('role', 'tab');
        tab.id = panel.id + '-tab'; tab.setAttribute('aria-controls', panel.id);
        tab.textContent = panel.dataset.title || ('Tab ' + (i + 1));
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n, true); this.tabs[n].focus();
          }
        });
        this.bar.appendChild(tab);
        return tab;
      });
      this.onToggle = () => this.paint();
      this.panels.forEach((p) => p.addEventListener('toggle', this.onToggle));
      this.onMedia = () => this.apply();
      this.mq.addEventListener('change', this.onMedia);
      this.apply();
    }
    disconnectedCallback() {
      if (this.mq) this.mq.removeEventListener('change', this.onMedia);
      (this.panels || []).forEach((p) => p.removeEventListener('toggle', this.onToggle));
    }
    apply() {
      this.classList.toggle('oa-ptabs--js', this.mq.matches);
      if (this.mq.matches) {
        let open = this.panels.findIndex((p) => p.open);
        if (open < 0) open = 0;
        this.select(open, false);
      }
      this.paint();
    }
    select(index, focusPanel) {
      this.panels.forEach((p, i) => { p.open = i === index; });
      this.paint();
      if (focusPanel && this.mq.matches) {
        const top = this.bar.getBoundingClientRect().top;
        if (top < 0) this.bar.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
    paint() {
      this.panels.forEach((p, i) => {
        const tab = this.tabs[i];
        tab.setAttribute('aria-selected', p.open ? 'true' : 'false');
        tab.tabIndex = p.open ? 0 : -1;
      });
    }
  }
  if (!customElements.get('onyx-tabs')) customElements.define('onyx-tabs', OnyxTabs);

  /* <onyx-delivery> — turns "7–10 business days" into a date range, skipping weekends and the
     bank holidays listed on the block. */
  class OnyxDelivery extends HTMLElement {
    connectedCallback() {
      const target = this.querySelector('[data-oa-range]');
      if (!target) return;
      const min = parseInt(this.dataset.min, 10) || 1;
      const max = Math.max(min, parseInt(this.dataset.max, 10) || min);
      const holidays = new Set((this.dataset.holidays || '').split(/[,\s]+/).filter(Boolean));
      const iso = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const isBusiness = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !holidays.has(iso(d));
      const addBusiness = (n) => {
        const d = new Date(); d.setHours(12, 0, 0, 0);
        let left = n;
        while (left > 0) { d.setDate(d.getDate() + 1); if (isBusiness(d)) left -= 1; }
        return d;
      };
      const from = addBusiness(min), to = addBusiness(max);
      const fmt = (d, withMonth) => d.toLocaleDateString('en-GB', withMonth ? { weekday: 'short', day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric' });
      const sameMonth = from.getMonth() === to.getMonth();
      target.textContent = (sameMonth ? fmt(from, false) : fmt(from, true)) + ' – ' + fmt(to, true);
      target.setAttribute('title', min + '–' + max + ' business days');
    }
  }
  if (!customElements.get('onyx-delivery')) customElements.define('onyx-delivery', OnyxDelivery);

  /* Variant sync — Horizon refreshes only its own blocks when the variant changes. Onyx blocks
     that depend on the selected variant (bundle prices, savings, part number, stock & delivery)
     carry data-oa-sync="<key>"; when the variant request resolves, each one is swapped for its
     freshly rendered copy from the same response. */
  document.addEventListener('shopify:product:select', (event) => {
    const section = event.target instanceof Element ? event.target.closest('.shopify-section, dialog') : null;
    if (!section || !event.promise || (event.target.closest && event.target.closest('product-card'))) return;
    event.promise.then((result) => {
      const html = result && result.detail && result.detail.html;
      if (!html || typeof html.querySelector !== 'function') return;
      section.querySelectorAll('[data-oa-sync]').forEach((el) => {
        const key = el.getAttribute('data-oa-sync');
        const fresh = html.querySelector('[data-oa-sync="' + key + '"]');
        if (fresh) el.replaceWith(document.importNode(fresh, true));
      });
    }).catch(() => {});
  }, true);

  /* Deep links such as /pages/faq#delivery-times open the matching answer. */
  function openFromHash() {
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target && target.matches('details[data-faq-item]')) target.open = true;
  }
  window.addEventListener('hashchange', openFromHash);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openFromHash); else openFromHash();
})();
